import { ReactNode, useEffect } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { toast } from "sonner";

import { ScreenLoading } from "@/blocks/ScreenLoading";
import { useSignOutMutation } from "@/data/mutations/authMutations";
import { useCurrentStaffQuery } from "@/data/queries/staffQueries";
import { useCurrentStudentQuery } from "@/data/queries/studentQueries";
import { useTranslator } from "@/hooks/useTranslate";
import { envVariables } from "@/utils/envVariables";

import { CurrentUserContext } from "./CurrentUserContext";

export const CurrentUserContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { t } = useTranslator();
  const { mutate: signOut } = useSignOutMutation();

  const { data: staffUser, error: staffError } = useCurrentStaffQuery();

  const { data: studentUser, error: studentError } = useCurrentStudentQuery();

  const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader({
    id: "google-maps-script",
    googleMapsApiKey: envVariables.VITE_GOOGLE_MAP_API,
    libraries: ["places"],
  });

  useEffect(() => {
    if (staffError || studentError) {
      // On an error (usually from not having a corresponding staff|student in the database)
      // Display an error message and sign out the user
      toast.error(t("main:login.errors.unauthorized"), {
        duration: 10000,
      });
      signOut();
    }
  }, [staffError, studentError, t, signOut]);

  useEffect(() => {
    if (staffUser?.status === "DEACTIVATED") {
      toast.error(t("main:login.errors.accountDeactivated"), {
        duration: 10000,
      });
      signOut();
    }
  }, [staffUser?.status, t, signOut]);

  // Only render the children when we have the user data and the staff is not deactivated
  if (
    (staffUser || studentUser) &&
    isGoogleMapsLoaded &&
    staffUser?.status !== "DEACTIVATED"
  ) {
    return (
      <CurrentUserContext.Provider value={staffUser ?? studentUser}>
        {children}
      </CurrentUserContext.Provider>
    );
  }

  return <ScreenLoading />;
};
